import React, { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Check, Clock, ArrowRight, X } from "lucide-react";
import { availability, timeSlots } from "../mock";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function buildGrid(year, month) {
  const first = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const lead = (first.getDay() + 6) % 7; // Mon-first
  const cells = [];
  for (let i = 0; i < lead; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

export default function BookingCalendar({ onConfirm, confirmLabel = "Request this slot" }) {
  const months = availability.months;

  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const isBookable = (mo, day) =>
    mo.freeDays.includes(day) && new Date(mo.year, mo.month - 1, day) >= today;

  // Start on the first month that still has a bookable day
  const initialIdx = useMemo(() => {
    const i = months.findIndex((mo) => mo.freeDays.some((d) => isBookable(mo, d)));
    return i === -1 ? 0 : i;
  }, []); // eslint-disable-line

  const [idx, setIdx] = useState(initialIdx);
  const [selected, setSelected] = useState(null); // { monthKey, day, label }
  const [time, setTime] = useState("");

  const m = months[idx];
  const grid = buildGrid(m.year, m.month);
  const customValue = time && !timeSlots.includes(time) ? time : "";

  const go = (dir) => {
    setIdx((v) => Math.min(months.length - 1, Math.max(0, v + dir)));
    setSelected(null);
    setTime("");
  };

  const pick = (day) => {
    if (!isBookable(m, day)) return;
    setSelected({ monthKey: m.key, day, label: `${day} ${m.label}` });
    setTime("");
  };

  const confirm = () => {
    if (!selected || !time) return;
    onConfirm(selected.label, time);
  };

  return (
    <div className="grid gap-8 lg:grid-cols-12">
      {/* Calendar */}
      <div className="lg:col-span-7">
        <div className="rounded-3xl border border-black/10 bg-brand-cream p-5 sm:p-7 shadow-xl shadow-black/5">
          <div className="flex items-center justify-between">
            <button
              onClick={() => go(-1)}
              disabled={idx === 0}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-brand-ink transition-colors hover:bg-brand-stone disabled:opacity-30"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h3 className="font-display text-xl sm:text-2xl font-semibold">{m.label}</h3>
            <button
              onClick={() => go(1)}
              disabled={idx === months.length - 1}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-brand-ink transition-colors hover:bg-brand-stone disabled:opacity-30"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          <div className="mt-5 grid grid-cols-7 gap-1.5 sm:gap-2">
            {WEEKDAYS.map((w) => (
              <div key={w} className="py-1 text-center text-[10px] sm:text-xs font-semibold uppercase tracking-wider text-brand-ink/40">
                {w}
              </div>
            ))}
          </div>

          <div className="mt-1.5 grid grid-cols-7 gap-1.5 sm:gap-2">
            {grid.map((day, i) => {
              if (day === null) return <div key={i} />;
              const cellDate = new Date(m.year, m.month - 1, day);
              const isPast = cellDate < today;
              const free = m.freeDays.includes(day);
              const bookable = free && !isPast;
              const isSel = selected && selected.monthKey === m.key && selected.day === day;
              return (
                <button
                  key={i}
                  onClick={() => pick(day)}
                  disabled={!bookable}
                  aria-label={`${day} ${m.label}${bookable ? " free" : " unavailable"}`}
                  className={[
                    "relative flex aspect-square flex-col items-center justify-center rounded-lg sm:rounded-xl text-sm font-medium transition-all duration-200",
                    bookable
                      ? "bg-brand-tealsoft text-brand-tealdark hover:bg-brand-teal hover:text-white cursor-pointer"
                      : "bg-brand-concrete/40 text-brand-ink/30 cursor-not-allowed",
                    isSel ? "ring-2 ring-brand-teal ring-offset-2 ring-offset-brand-cream !bg-brand-teal !text-white" : "",
                  ].join(" ")}
                >
                  <span className={!bookable ? "opacity-60" : ""}>{day}</span>
                  {bookable && (
                    <span className="mt-0.5 hidden sm:block text-[9px] font-semibold uppercase tracking-wide opacity-70">Free</span>
                  )}
                  {!bookable && isPast && (
                    <X className="pointer-events-none absolute h-5 w-5 sm:h-6 sm:w-6 text-brand-ink/25" strokeWidth={1.6} />
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-5 text-xs text-brand-ink/60">
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-brand-tealsoft border border-brand-teal/30" /> Free to book</span>
            <span className="flex items-center gap-2"><span className="h-3 w-3 rounded bg-brand-concrete/60" /> Booked / away</span>
            <span className="flex items-center gap-2"><X className="h-3.5 w-3.5 text-brand-ink/40" /> Date passed</span>
          </div>
        </div>
      </div>

      {/* Booking panel */}
      <div className="lg:col-span-5">
        <div className="flex h-full flex-col rounded-3xl bg-brand-ink p-7 text-white">
          <h3 className="font-display text-xl font-semibold">Book a time</h3>
          {!selected ? (
            <p className="mt-3 text-sm leading-relaxed text-white/60">
              Select a day on the calendar to see available times.
            </p>
          ) : (
            <div className="mt-4 flex flex-1 flex-col">
              <div className="flex items-center gap-2 rounded-xl bg-white/5 px-4 py-3">
                <Check className="h-4 w-4 text-brand-teal" strokeWidth={2.6} />
                <span className="text-sm font-medium">{selected.label}</span>
              </div>

              <p className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50">
                <Clock className="h-3.5 w-3.5" /> Preferred start time
              </p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {timeSlots.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTime(t)}
                    className={[
                      "rounded-lg px-2 py-2.5 text-sm font-medium transition-colors",
                      time === t ? "bg-brand-teal text-white" : "bg-white/5 text-white/80 hover:bg-white/10",
                    ].join(" ")}
                  >
                    {t}
                  </button>
                ))}
              </div>

              <div className="mt-3">
                <label className="text-xs font-medium text-white/50">Or type a custom time</label>
                <input
                  type="text"
                  value={customValue}
                  onChange={(e) => setTime(e.target.value)}
                  placeholder="e.g. 8:30 AM or early morning"
                  className="mt-1.5 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 outline-none focus:border-brand-teal"
                />
              </div>

              <button
                onClick={confirm}
                disabled={!time}
                className="group mt-6 lg:mt-auto inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal px-6 py-4 text-sm font-semibold text-white transition-all duration-300 hover:bg-white hover:text-brand-ink disabled:opacity-40 disabled:hover:bg-brand-teal disabled:hover:text-white"
              >
                {confirmLabel}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
